import { UzButton } from '@utazzo/ui';
import { AlertTriangle, CheckCircle2, RefreshCw } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';

import type { DestinationReelPreview } from '@utazzo/shared';

import { previewReel } from '@/lib/reel-preview';

export type ReelCheckState =
  | { phase: 'idle' }
  | { phase: 'checking' }
  | { phase: 'done'; preview: DestinationReelPreview };

/**
 * On-demand check of a reel link against the provider, run from the Fetch
 * button rather than on every keystroke - hitting Instagram once per edit is
 * what keeps it from rate-limiting us.
 *
 * Editing the URL clears the last verdict, so a green border can never be left
 * over from a link that is no longer in the box.
 */
export const useReelCheck = (url: string) => {
  const [state, setState] = useState<ReelCheckState>({ phase: 'idle' });
  // Bumped on every edit and every new check, so a slow response that lands
  // after the editor moved on is discarded instead of overwriting the field.
  const generation = useRef(0);

  useEffect(() => {
    generation.current += 1;
    setState({ phase: 'idle' });
  }, [url]);

  const check = (): void => {
    const trimmed = url.trim();
    if (!trimmed) return;
    const token = (generation.current += 1);
    setState({ phase: 'checking' });
    previewReel(trimmed)
      .then((preview) => {
        if (generation.current === token) setState({ phase: 'done', preview });
      })
      .catch(() => {
        if (generation.current === token) setState({ phase: 'idle' });
      });
  };

  return { state, check };
};

/** Green = fetched, amber = valid but no cover. Red is left to the field's error slot. */
const BORDER_BY_STATUS: Record<DestinationReelPreview['status'], string | undefined> = {
  ok: 'border-emerald-500 focus-visible:border-emerald-500',
  no_cover: 'border-amber-500 focus-visible:border-amber-500',
  not_found: undefined,
  invalid: undefined,
};

/** Border class for the URL input, or undefined when there is no verdict yet. */
export const reelCheckBorderClass = (state: ReelCheckState): string | undefined =>
  state.phase === 'done' ? BORDER_BY_STATUS[state.preview.status] : undefined;

/**
 * Error message for the URL input, or undefined. Only a definitive provider
 * verdict turns the field red - a blocked or slow check must never look like a
 * broken link (see `no_cover` in DestinationReelPreviewSchema).
 */
export const reelCheckError = (state: ReelCheckState): string | undefined => {
  if (state.phase !== 'done') return undefined;
  const { status, message } = state.preview;
  if (status !== 'not_found' && status !== 'invalid') return undefined;
  return message ?? 'This reel link could not be resolved.';
};

/** Provider the last check identified, for provider-specific hints. */
export const reelCheckProvider = (
  state: ReelCheckState,
): DestinationReelPreview['provider'] | null =>
  state.phase === 'done' ? state.preview.provider : null;

const ReelCheckVerdict = ({ state }: { state: ReelCheckState }): React.JSX.Element | null => {
  if (state.phase !== 'done') return null;
  const { status, title, message, provider } = state.preview;

  if (status === 'ok') {
    return (
      <p className="flex items-start gap-1.5 text-xs font-medium text-emerald-600">
        <CheckCircle2 className="mt-0.5 h-3.5 w-3.5 shrink-0" />
        <span className="line-clamp-2">
          {title ?? `${provider === 'youtube' ? 'YouTube' : 'Instagram'} reel found`}
        </span>
      </p>
    );
  }

  if (status === 'no_cover') {
    return (
      <p className="flex items-start gap-1.5 text-xs font-medium text-amber-600">
        <AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0" />
        <span>{message}</span>
      </p>
    );
  }

  return null; // not_found / invalid surface through the input's error slot.
};

/**
 * Fetch button + verdict, sat on their own row under the URL input so the
 * input's height never shifts when an error message appears beneath it.
 */
export const ReelCheckBar = ({
  state,
  onFetch,
  url,
  disabled,
}: {
  state: ReelCheckState;
  onFetch: () => void;
  url: string;
  disabled?: boolean;
}): React.JSX.Element => (
  <div className="flex items-center gap-2">
    <UzButton
      type="button"
      variant="outline"
      size="sm"
      className="shrink-0"
      loading={state.phase === 'checking'}
      disabled={disabled || !url.trim()}
      onClick={onFetch}
    >
      <RefreshCw className="mr-1.5 h-3.5 w-3.5" />
      Fetch
    </UzButton>
    <ReelCheckVerdict state={state} />
  </div>
);
